import type { NotificationData } from '../../services/notification.service';
import { applyResolvedLprEntryTypeToNotification } from '../../notifications/notification.utils';
import {
  buildNotificationDataForModal,
  getLicensePlateFromNotification,
} from './notification-display.util';

/** Re-open window after reload for embedded WebViews that miss the live SSE popup. */
export const LPR_NOTIFICATION_REHYDRATE_MAX_AGE_MS = 90_000;

/** Persist dismiss timestamps across reload so rehydration does not reopen closed modals. */
export const LPR_NOTIFICATION_DISMISSED_SESSION_KEY = 'lpr_notification_dismissed_at';

export const LPR_NOTIFICATION_DISMISSED_TTL_MS = 5 * 60 * 1000;

export const NOTIFICATION_HISTORY_STORAGE_KEY = 'notification_history';

export interface StoredNotificationEntry {
  event: string;
  data: NotificationData;
  timestamp: string;
}

function getLprEntryIdFromNotificationData(data: Record<string, any> | null | undefined): string {
  if (!data) {
    return '';
  }
  const nested = data['data'] as Record<string, any> | undefined;
  return String(
    data['lpr_entry_id'] ||
      data['lprEntryId'] ||
      data['id'] ||
      data['licence_plate_id'] ||
      data['license_plate_id'] ||
      nested?.['lpr_entry_id'] ||
      nested?.['id'] ||
      ''
  ).trim();
}

export function getLprEntryIdFromNotification(
  notification: NotificationData | null | undefined
): string {
  return getLprEntryIdFromNotificationData(notification?.data as Record<string, any> | undefined);
}

export function getLprEntrySuppressionKeysFromNotification(
  notification: NotificationData | null | undefined
): string[] {
  const entryId = getLprEntryIdFromNotificationData(
    notification?.data as Record<string, any> | undefined
  );
  return entryId ? [`entry:${entryId}`] : [];
}

export function readPersistedDismissedLprKeys(now = Date.now()): Map<string, number> {
  const dismissed = new Map<string, number>();
  if (typeof sessionStorage === 'undefined') {
    return dismissed;
  }

  try {
    const raw = sessionStorage.getItem(LPR_NOTIFICATION_DISMISSED_SESSION_KEY);
    if (!raw) {
      return dismissed;
    }
    const parsed = JSON.parse(raw) as Record<string, any>;
    if (!parsed || typeof parsed !== 'object') {
      return dismissed;
    }

    let changed = false;
    for (const [key, value] of Object.entries(parsed)) {
      const dismissedAt = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(dismissedAt) || now - dismissedAt >= LPR_NOTIFICATION_DISMISSED_TTL_MS) {
        changed = true;
        continue;
      }
      dismissed.set(key, dismissedAt);
    }

    if (changed) {
      writePersistedDismissedLprKeys(dismissed);
    }
  } catch {
    sessionStorage.removeItem(LPR_NOTIFICATION_DISMISSED_SESSION_KEY);
  }

  return dismissed;
}

function writePersistedDismissedLprKeys(dismissed: Map<string, number>): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  if (dismissed.size === 0) {
    sessionStorage.removeItem(LPR_NOTIFICATION_DISMISSED_SESSION_KEY);
    return;
  }
  sessionStorage.setItem(
    LPR_NOTIFICATION_DISMISSED_SESSION_KEY,
    JSON.stringify(Object.fromEntries(dismissed))
  );
}

export function persistDismissedLprEntryKeys(keys: string[], dismissedAt = Date.now()): void {
  if (!keys.length || typeof sessionStorage === 'undefined') {
    return;
  }

  const dismissed = readPersistedDismissedLprKeys(dismissedAt);
  keys.forEach(key => dismissed.set(key, dismissedAt));
  writePersistedDismissedLprKeys(dismissed);
}

export function clearPersistedDismissedLprEntryKeys(keys: string[]): void {
  if (!keys.length || typeof sessionStorage === 'undefined') {
    return;
  }

  const dismissed = readPersistedDismissedLprKeys();
  keys.forEach(key => dismissed.delete(key));
  writePersistedDismissedLprKeys(dismissed);
}

/**
 * Notification types that report a finished outcome and only offer "Okay". Rehydration exists
 * so an attendant does not lose a prompt that still needs a decision when the page reloads;
 * replaying a result they already saw is the CWS-612 bug (refresh after placing an order
 * re-opened "Order Success!"). `active-subscription` is deliberately not listed: the lane
 * auto-redeem path re-shows it as a toast on purpose (CWS-1053).
 */
export const TERMINAL_OUTCOME_NOTIFICATION_TYPES: ReadonlySet<string> = new Set([
  'order-success',
  'order-completed',
]);

export function isTerminalOutcomeStoredNotification(entry: StoredNotificationEntry): boolean {
  const type = String(entry.data?.type ?? entry.event ?? '').trim();
  return TERMINAL_OUTCOME_NOTIFICATION_TYPES.has(type);
}

export function isStaffInitiatedStoredNotification(entry: StoredNotificationEntry): boolean {
  const data = (entry.data?.data ?? {}) as Record<string, any>;
  return data['quick_plus_order'] === true || data['isBuyPackageFlow'] === true;
}

/**
 * A manual image scan is something the user started, so the live-SSE reasoning behind
 * rehydration does not apply: nothing was missed by the reload, and the user can simply
 * scan again. The dismissal written on close lives in sessionStorage, which the Android
 * shell's WebView drops when it is recreated on refresh, while the history row that drives
 * the reopen lives in localStorage and survives — so on a tablet nothing stopped the reload
 * from re-opening a popup the user had just closed (CWS-863).
 */
export function isManualCaptureStoredNotification(entry: StoredNotificationEntry): boolean {
  // Multi-eligible choosers also carry `is_lpr_upload`, but as a routing flag rather than
  // because anyone uploaded an image — and CWS-889 added a DB-row fallback precisely because
  // their SSE is often missed. They are a mandatory attendant choice, so they must still
  // recover after a reload; only genuine manual captures are excluded here.
  if (entry.data?.type === 'multi-eligible-selection') {
    return false;
  }
  // `is_lpr_upload` alone, not `isManualLprCaptureNotification`: lane prepaid-redeem SSE
  // prompts also carry `source_context: 'lpr'` and must still recover after a reload.
  const data = (entry.data?.data ?? {}) as Record<string, any>;
  return data['is_lpr_upload'] === true || isManualCaptureLprEntryRow(data);
}

/**
 * The backend's own record that a row came from a staff image scan: `POST order/get-lp-image`
 * stamps `image_source: 'webflow'` on the lpr_entries row, and it travels with every copy
 * of that row (SSE payloads, `GET order/lpr-entries/:id`). `is_lpr_upload` is stamped by this
 * app on the HTTP response only, so a copy that arrived another way does not carry it.
 */
export function isManualCaptureLprEntryRow(row: Record<string, any> | null | undefined): boolean {
  if (!row) {
    return false;
  }
  const source = row['image_source'] ?? row['imageSource'];
  return typeof source === 'string' && source.trim().toLowerCase() === 'webflow';
}

export function isStoredNotificationDismissedForRehydrate(
  entry: StoredNotificationEntry,
  now = Date.now()
): boolean {
  const keys = getLprEntrySuppressionKeysFromNotification(entry.data);
  if (!keys.length) {
    return false;
  }
  const dismissed = readPersistedDismissedLprKeys(now);
  return keys.some(key => {
    const dismissedAt = dismissed.get(key);
    return !!dismissedAt && now - dismissedAt < LPR_NOTIFICATION_DISMISSED_TTL_MS;
  });
}

export function removeStoredNotificationFromHistory(notification: NotificationData): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  try {
    const raw = localStorage.getItem(NOTIFICATION_HISTORY_STORAGE_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return;
    }

    const notifications = parsed as StoredNotificationEntry[];
    const entryId = getLprEntryIdFromNotificationData(
      notification.data as Record<string, any> | undefined
    );
    const licensePlate = getLicensePlateFromNotification(notification);
    const normalizedPlate = licensePlate.trim().toUpperCase();

    const filtered = notifications.filter(existing => {
      const existingData = existing?.data;
      if (!existingData) {
        return true;
      }

      if (entryId) {
        const existingEntryId = getLprEntryIdFromNotificationData(
          existingData.data as Record<string, any> | undefined
        );
        return existingEntryId !== entryId;
      }

      if (normalizedPlate && normalizedPlate !== 'UNKNOWN') {
        const existingPlate = getLicensePlateFromNotification(existingData).trim().toUpperCase();
        return existingPlate !== normalizedPlate;
      }

      return true;
    });

    if (filtered.length === notifications.length) {
      return;
    }

    if (filtered.length === 0) {
      localStorage.removeItem(NOTIFICATION_HISTORY_STORAGE_KEY);
    } else {
      localStorage.setItem(NOTIFICATION_HISTORY_STORAGE_KEY, JSON.stringify(filtered));
    }

    window.dispatchEvent(
      new CustomEvent('notificationStored', {
        detail: { notificationCount: filtered.length },
      })
    );
  } catch {
    // Ignore storage failures — rehydration guards still apply via sessionStorage.
  }
}

export function readMostRecentStoredLprNotification(): StoredNotificationEntry | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(NOTIFICATION_HISTORY_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return null;
    }
    const [newest] = parsed as StoredNotificationEntry[];
    return newest?.data ? newest : null;
  } catch {
    return null;
  }
}

export function shouldRehydrateStoredLprNotification(
  entry: StoredNotificationEntry | null,
  now = Date.now()
): entry is StoredNotificationEntry {
  if (!entry?.timestamp || !entry.data) {
    return false;
  }
  if (isStaffInitiatedStoredNotification(entry)) {
    return false;
  }
  if (isManualCaptureStoredNotification(entry)) {
    return false;
  }
  if (isTerminalOutcomeStoredNotification(entry)) {
    return false;
  }
  if (isStoredNotificationDismissedForRehydrate(entry, now)) {
    return false;
  }
  const ageMs = now - new Date(entry.timestamp).getTime();
  return Number.isFinite(ageMs) && ageMs >= 0 && ageMs <= LPR_NOTIFICATION_REHYDRATE_MAX_AGE_MS;
}

/** Row of `GET order/lpr-entries/:id`; the API wraps it as `data` or `data.data`. */
export function extractLprEntryRowFromApiResponse(response: any): Record<string, any> | null {
  if (!response) {
    return null;
  }
  const ok =
    response.status === true ||
    response.status_code === 200 ||
    (response.status !== false && response.data != null);
  if (!ok || response.data == null) {
    return null;
  }
  const row = response.data?.data ?? response.data;
  return row && typeof row === 'object' && !Array.isArray(row)
    ? (row as Record<string, any>)
    : null;
}

/**
 * Re-resolve a rehydrated notification against the lane entry's *current* row.
 *
 * The stored copy is a snapshot from when the prompt first appeared (often a lookup miss or
 * an order request). By the time the page reloads the same entry may have been paid, and the
 * modal, which reloads the row on open, would then render "Order Success!" (CWS-612). Returns
 * `null` when the current state is a finished outcome, so the caller skips the reopen.
 *
 * Also `null` when the row itself says it was a staff image scan (CWS-863). The stored copy
 * is not always the one this app stamped `is_lpr_upload` on: when the scanned customer holds
 * a membership the backend redeems it and fans out `active-subscription` over SSE, and that
 * copy replaces the stamped one in history (same plate), so the stored-row guard in
 * `isManualCaptureStoredNotification` never sees the flag. The entry row is the one record
 * that always knows. Multi-eligible choosers keep recovering, as above.
 */
export function resolveRehydratedNotificationFromEntry(
  notification: NotificationData,
  entryRow: Record<string, any> | null
): NotificationData | null {
  const resolved = entryRow
    ? applyResolvedLprEntryTypeToNotification(notification, entryRow)
    : notification;
  if (TERMINAL_OUTCOME_NOTIFICATION_TYPES.has(String(resolved.type))) {
    return null;
  }
  if (isManualCaptureLprEntryRow(entryRow) && resolved.type !== 'multi-eligible-selection') {
    return null;
  }
  return resolved;
}

export function buildRehydratedLprNotification(entry: StoredNotificationEntry): NotificationData {
  return applyResolvedLprEntryTypeToNotification(buildNotificationDataForModal(entry));
}
